import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Loader2, CheckCircle2, MessageSquareText, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { submitFeedback, getConfig, api } from "@/lib/api";

export default function Feedback() {
  const { id } = useParams();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [config, setConfig] = useState(null);
  const [audience, setAudience] = useState("gruender");
  const isBusiness = audience === "business";

  useEffect(() => {
    getConfig().then(setConfig).catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/report/${id}/status`).then((r) => { if (r.data?.audience) setAudience(r.data.audience); }).catch(() => {});
  }, [id]);

  const submit = async () => {
    if (!rating && !message.trim()) {
      toast.warning("Bitte gib eine Bewertung ab oder schreibe uns kurz dein Feedback.");
      return;
    }
    setSending(true);
    try {
      await submitFeedback({ report_id: id || "", rating: rating || null, message: message.trim(), email: email.trim() });
      setSent(true);
    } catch (e) {
      toast.error("Feedback konnte nicht gesendet werden. Bitte versuche es erneut.");
    } finally {
      setSending(false);
    }
  };

  const reviewUrl = config?.review_enabled ? config?.review_url : "";
  const reviewProvider = config?.review_provider || "Bewertungsportal";

  return (
    <div className={`min-h-screen bg-slate-50 ${isBusiness ? "theme-petrol" : ""}`}>
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-center">
          <Logo size="sm" variant={isBusiness ? "business" : "founder"} />
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {!sent ? (
          <div
            data-testid="feedback-card"
            className="bg-white rounded-3xl border border-slate-200/70 shadow-sm p-6 sm:p-10"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold brand-gradient-text mb-3">
              <MessageSquareText className="h-4 w-4" style={{ color: "var(--brand-solid)" }} /> Dein Feedback
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
              Wie war deine Erfahrung mit Gründio?
            </h1>
            <p className="text-slate-600 mt-3 leading-relaxed">
              Dein ehrliches Feedback hilft uns direkt weiter – egal ob Lob, Kritik oder ein Verbesserungsvorschlag.
              Es wird vertraulich behandelt und geht nur an unser Team.
            </p>

            <div className="mt-8">
              <div className="text-sm font-medium text-slate-700 mb-3">Deine Bewertung</div>
              <div className="flex items-center gap-2" data-testid="feedback-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`feedback-star-${n}`}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    className="transition-transform hover:scale-110"
                    aria-label={`${n} Sterne`}
                  >
                    <Star
                      className="h-9 w-9"
                      style={{
                        color: (hover || rating) >= n ? "#F59E0B" : "#CBD5E1",
                        fill: (hover || rating) >= n ? "#F59E0B" : "transparent",
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label className="text-sm font-medium text-slate-700 mb-2 block">Deine Nachricht</label>
              <Textarea
                data-testid="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Was hat dir gefallen? Was können wir besser machen?"
                className="min-h-[130px] rounded-xl"
              />
            </div>

            <div className="mt-4">
              <label className="text-sm font-medium text-slate-700 mb-2 block">E-Mail (optional, für Rückfragen)</label>
              <Input
                data-testid="feedback-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="dein@email.de"
                className="h-12 rounded-xl"
              />
            </div>

            <Button
              data-testid="feedback-submit-btn"
              onClick={submit}
              disabled={sending}
              className="mt-8 h-14 w-full rounded-xl font-semibold text-base brand-gradient-bg text-white hover:opacity-95"
            >
              {sending ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Wird gesendet…</>
              ) : (
                "Feedback absenden"
              )}
            </Button>
          </div>
        ) : (
          <div
            data-testid="feedback-thanks"
            className="bg-white rounded-3xl border border-slate-200/70 shadow-sm p-6 sm:p-10 text-center"
          >
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="h-9 w-9" style={{ color: "var(--brand-solid)" }} />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
              Vielen Dank für dein Feedback!
            </h1>
            <p className="text-slate-600 mt-3 leading-relaxed">
              Wir lesen jede Nachricht persönlich und nutzen sie, um Gründio kontinuierlich zu verbessern.
            </p>

            {reviewUrl && (
              <div className="mt-8 pt-8 border-t border-slate-200">
                <p className="text-slate-700 mb-4">
                  Würdest du uns öffentlich weiterempfehlen? Das hilft anderen Gründern sehr.
                </p>
                <Button
                  asChild
                  data-testid="feedback-review-btn"
                  className="h-12 px-6 rounded-xl font-semibold brand-gradient-bg text-white hover:opacity-95"
                >
                  <a href={reviewUrl} target="_blank" rel="noreferrer">
                    Auf {reviewProvider} bewerten <ExternalLink className="h-4 w-4 ml-2" />
                  </a>
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
